import React from 'react';
import { useTranslation } from 'localization';
import SphereIdentityAtom from '../../../atoms/sphere-identity/SphereIdentityAtom';
import './HistoryCardMolecule.scss';

export interface HistoryCardEntry {
  periodId: string;
  /** Preferred display label for the period (e.g. "Q3 2025"). Falls back to a formatted `dueAt`. */
  periodLabel?: string;
  dueAt: string;
  checkedInAt: string | null;
  status: string | null;
  /** Period passed with no check-in. */
  isMissed?: boolean;
  /** Period was explicitly skipped. Also covered by `status === 'skipped'`. */
  isSkipped?: boolean;
  checkedInBy?: { sphereId: string; name: string; imageId?: string } | null;
  comment?: string | null;
}

export interface HistoryCardStatusDescriptor {
  /** CSS color (or `var(--token)`) for the status icon/dot. */
  fill: string;
  /** PrimeIcons class string (e.g. "pi pi-check-circle"). */
  icon: string;
  /** Pre-localized status label (e.g. "On Track"). */
  label: string;
}

export interface HistoryCardMoleculeProps {
  entry: HistoryCardEntry;
  /**
   * Status key → icon / color / localized-label descriptor. Consumer owns the
   * mapping so the molecule stays palette-agnostic. Keys typically include:
   * `on_track`, `at_risk`, `needs_attention`, `critical`, `blocked`,
   * `skipped`, `missed`.
   */
  statusColorMap: Record<string, HistoryCardStatusDescriptor>;
  /**
   * When true, the card renders a "Latest" tag and a subtle blue ring.
   * The strip passes `index === 0` after reversing entries to newest-first.
   */
  isLatest?: boolean;
  /** Override the locale used for date formatting (defaults to browser locale). */
  locale?: string;
  className?: string;
}

type HistoryCardVariant = 'checked-in' | 'missed' | 'skipped' | 'not-yet-due';

function resolveVariant(entry: HistoryCardEntry): HistoryCardVariant {
  if (entry.isMissed) return 'missed';
  if (entry.isSkipped || entry.status === 'skipped') return 'skipped';
  if (entry.checkedInAt && entry.status) return 'checked-in';
  return 'not-yet-due';
}

function formatDate(iso: string | null | undefined, locale?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const HistoryCardMolecule: React.FC<HistoryCardMoleculeProps> = ({
  entry,
  statusColorMap,
  isLatest = false,
  locale,
  className = '',
}) => {
  const { t } = useTranslation();
  const variant = resolveVariant(entry);

  const lookupKey =
    variant === 'missed'
      ? 'missed'
      : variant === 'skipped'
        ? 'skipped'
        : (entry.status ?? '');
  const descriptor: HistoryCardStatusDescriptor | undefined =
    statusColorMap[lookupKey];

  const periodLabel =
    entry.periodLabel && entry.periodLabel.length > 0
      ? entry.periodLabel
      : formatDate(entry.dueAt, locale);
  const dueLabel = formatDate(entry.dueAt, locale);

  let statusText: string;
  if (variant === 'missed') {
    statusText = t('taskList.historyStrip.missedNoLog');
  } else if (variant === 'skipped') {
    statusText = t('taskList.historyStrip.skipped');
  } else if (variant === 'not-yet-due') {
    statusText = t('taskList.historyStrip.notYetDue');
  } else {
    statusText = descriptor?.label ?? '';
  }

  const showSubmitterPanel =
    variant !== 'missed' &&
    variant !== 'not-yet-due' &&
    Boolean(entry.checkedInBy?.name);
  const showNote = variant !== 'missed' && variant !== 'not-yet-due';
  const hasComment =
    typeof entry.comment === 'string' && entry.comment.length > 0;

  const classes = [
    'history-card-molecule',
    `history-card-molecule--${variant}`,
    isLatest ? 'history-card-molecule--latest' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const iconClassName = [
    'history-card-molecule__pill-icon',
    descriptor?.icon ?? 'pi pi-circle-fill',
  ].join(' ');

  // Inline pill tinting: derive a soft tinted background and a slightly
  // stronger border from the status fill color via color-mix in srgb so the
  // pill works for any status (descriptor is consumer-provided).
  const pillStyle: React.CSSProperties | undefined = descriptor
    ? {
        backgroundColor: `color-mix(in srgb, ${descriptor.fill} 12%, transparent)`,
        borderColor: `color-mix(in srgb, ${descriptor.fill} 36%, transparent)`,
      }
    : undefined;

  return (
    <article className={classes} aria-label={statusText}>
      <div className="history-card-molecule__top">
        <span
          className="history-card-molecule__status-pill"
          style={pillStyle}
        >
          <i
            className={iconClassName}
            style={descriptor ? { color: descriptor.fill } : undefined}
            aria-hidden="true"
          />
          <span className="history-card-molecule__status-label">
            {statusText}
          </span>
        </span>
        {isLatest && (
          <span className="history-card-molecule__latest-tag">
            {t('taskList.historyStrip.latest')}
          </span>
        )}
      </div>

      <div className="history-card-molecule__period-block">
        <span className="history-card-molecule__period">{periodLabel}</span>
        {dueLabel && (
          <span className="history-card-molecule__date">{dueLabel}</span>
        )}
      </div>

      {showSubmitterPanel && entry.checkedInBy && (
        <div className="history-card-molecule__submitter">
          <SphereIdentityAtom
            sphereId={entry.checkedInBy.sphereId}
            sphereName={entry.checkedInBy.name}
            imageId={entry.checkedInBy.imageId}
            size="xsmall"
            showInitials
            className="history-card-molecule__submitter-identity"
          />
          <div className="history-card-molecule__submitter-meta">
            <span className="history-card-molecule__submitter-label">
              {t('taskList.historyStrip.submittedBy')}
            </span>
            <span className="history-card-molecule__submitter-name">
              {entry.checkedInBy.name}
            </span>
          </div>
        </div>
      )}

      {variant === 'missed' && (
        <div
          className="history-card-molecule__missed-banner"
          role="note"
        >
          <i
            className="pi pi-exclamation-triangle history-card-molecule__missed-icon"
            aria-hidden="true"
          />
          <span>{t('taskList.historyStrip.noLogFiled')}</span>
        </div>
      )}

      {showNote && (
        <p
          className={
            hasComment
              ? 'history-card-molecule__note'
              : 'history-card-molecule__note history-card-molecule__note--empty'
          }
        >
          {hasComment
            ? `“${entry.comment}”`
            : t('taskList.historyStrip.noNotes')}
        </p>
      )}
    </article>
  );
};

HistoryCardMolecule.displayName = 'HistoryCardMolecule';

export default HistoryCardMolecule;
